import React, { useEffect, useState } from 'react'
import { Input, Button } from 'antd'
import { SearchOutlined } from '@ant-design/icons'
import classnames from 'classnames'
import { useDebouncedCallback } from 'use-debounce'
import closeIcon from 'static/img/ic-close.svg'
import ContentHeader from '../ContentHeader'
import { useI18n } from 'contexts/AuthContext'
import { fetchPhysicianPromise, mcaPcpPromise } from 'apis/FitOnCareAPI'
import _ from 'lodash'
import { usePrimaryPhysician } from 'hooks/careHooks'
import { isMobile, showNotification } from 'utils/basic'
import styles from './primaryCareProvider.module.scss'
import { useMCA } from './useMCA'
import { logAmplitudeEvent } from 'utils/amplitudeInit'

const PrimaryCareProvider = (props) => {
  const { sku, setCurrentKey } = props
  const { i18n } = useI18n()
  const { primaryPhysician } = usePrimaryPhysician()
  const [searchText, setSearchText] = useState('')
  const [physicianList, setPhysicianList] = useState([])
  const [selectedPhysician, setSelectedPhysician] = useState(
    primaryPhysician || []
  )
  const { openJoinScreen } = useMCA({ sku })

  useEffect(() => {
    logAmplitudeEvent('Screen View: Onboarding - PCP', {
      sku,
    })
  }, [sku])

  const fetchPhysicianList = useDebouncedCallback(async (text) => {
    if (text) {
      // npi_idv: a PCP is an individual provider, so search the individual NPI registry —
      // it returns real name.first/name.last (npi_org leaves them empty). See CARE-106.
      const list = await fetchPhysicianPromise(
        encodeURIComponent(text),
        'npi_idv'
      )
      //0 - NPI,
      //1 - name.first,
      //2 - name.last,
      //3 - name.full,
      //4 - provider_type,
      //5 - addr_practice.line1,
      //6 - addr_practice.line2,
      //7 - addr_practice.city,
      //8 - addr_practice.state,
      //9 - addr_practice.zip,
      //10 - addr_practice.country,
      //11 - addr_practice.full,
      //12 - addr_practice.phone,
      //13 - addr_practice.fax
      if (!_.isEmpty(list)) {
        setPhysicianList(list[3])
      }
    } else {
      clearSearchResults()
    }
  }, 500)

  const clearSearchResults = () => {
    setPhysicianList([])
  }

  const onBack = (e) => {
    if (!_.isEmpty(selectedPhysician)) {
      clearSearchResults()
      setSelectedPhysician({})
      setSearchText('')
    } else {
      props.onBack(e)
    }
  }

  const onContinue = async () => {
    logAmplitudeEvent('Click: Onboarding - PCP - Save', {
      sku,
      has_pcp: true,
    })
    const resp = await mcaPcpPromise({
      providerName: selectedPhysician[3],
      firstName: selectedPhysician[1],
      lastName: selectedPhysician[2],
      practice: selectedPhysician[11],
      npi: selectedPhysician[0],
      phone: selectedPhysician[12],
      fax: selectedPhysician[13],
      location: {
        line1: selectedPhysician[5],
        line2: selectedPhysician[6],
        city: selectedPhysician[7],
        state: selectedPhysician[8],
        zip: selectedPhysician[9],
        country: selectedPhysician[10],
      },
      acceptsInsurance: true,
    })
    if (resp.code === 200) {
      openNextScreen()
    } else {
      showNotification({
        type: 'error',
        message: resp.msg || i18n('Failed to add primary care provider'),
      })
    }
  }

  const openNextScreen = async () => {
    const screenKey = await openJoinScreen()
    if (screenKey) {
      setCurrentKey(screenKey)
    }
  }

  return (
    <div className={classnames(styles.styles, 'info-sec')}>
      <ContentHeader {...props} onBack={onBack} />
      <div
        className='info-content'
        style={isMobile() ? {} : { height: '750px' }}
      >
        <div className='title-pns'>
          {i18n('Add your primary care provider (optional)')}
        </div>
        <div className='search-sec'>
          <Input
            prefix={<SearchOutlined className='search-icon' />}
            rootClassName='search-input'
            placeholder={i18n('Search your doctor by name...')}
            suffix={
              searchText ? (
                <img
                  alt=''
                  src={closeIcon}
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    clearSearchResults()
                    setSelectedPhysician({})
                    setSearchText('')
                  }}
                />
              ) : null
            }
            onChange={(e) => {
              setSearchText(e.target.value)
              fetchPhysicianList(e.target.value)
            }}
            value={searchText}
            bordered={false}
          />
        </div>
        <div className='physician-results'>
          {!_.isEmpty(selectedPhysician) ? (
            <div className='physician-result-sec'>
              <div className='physician-result-card-name'>
                {selectedPhysician[3]}
              </div>
              <div className='physician-result-card-info'>
                {selectedPhysician[4]}
              </div>
              <div className='physician-result-card-info'>
                {selectedPhysician[5]}
              </div>
              <div className='physician-result-card-info'>
                {`${selectedPhysician[7]}, ${selectedPhysician[8]}`}
              </div>
              <div className='physician-result-card-info'>
                {selectedPhysician[12]}
              </div>
              <button
                className='remove-btn'
                onClick={() => {
                  setSelectedPhysician({})
                }}
              >
                {i18n('Remove')}
              </button>
            </div>
          ) : (
            <>
              {(physicianList || []).map((physician, index) => (
                <div
                  className='physician-result-item'
                  key={index}
                  onClick={() => {
                    setSelectedPhysician(physician)
                  }}
                >
                  <div className='physician-result-card-name'>
                    {physician[3]}
                  </div>
                  <div className='physician-result-card-info'>
                    {physician[4]}
                  </div>
                  <div className='physician-result-card-info'>
                    {physician[5]}
                  </div>
                  <div className='physician-result-card-info'>
                    {`${physician[7]}, ${physician[8]}`}
                  </div>
                  <div className='physician-result-card-info'>
                    {physician[12]}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>

        <div className='authorize-sec'>
          {i18n(
            'I authorize FitOn Care to share data with my primary care provider for coordinated care and understand I can revoke this anytime.'
          )}
        </div>
        <Button
          className='btn-primary full-width-btn m-t-16'
          onClick={onContinue}
          disabled={_.isEmpty(selectedPhysician)}
          style={{ width: '100%' }}
        >
          {i18n('Continue')}
        </Button>
        <button
          className='skip-btn'
          onClick={() => {
            logAmplitudeEvent('Click: Onboarding - PCP - Save', {
              sku,
              has_pcp: false,
            })
            openNextScreen()
          }}
        >
          {i18n('Skip')}
        </button>
      </div>
    </div>
  )
}

export default PrimaryCareProvider
